import rcolor from "rcolor";

import { COLOR_GREEN } from "style/theme";
import { getItemFromStorage } from "./storage";
import pythonSuggestions from "./pythonSuggestions.json";
import {
  AUTH_TOKEN_KEY,
  CHART_COLORS,
  DEPLOY_STATUS,
  FALLBACK_LANG,
  USER_TYPE_KEY,
  USER_PLAN_KEY,
  MONETIZATION_KEY,
} from "./constants";

export { pythonSuggestions };

const getGradientColor = (color1, color2, percentage) => {
  // Convert hex color to RGB
  const hexToRgb = (hex) => {
    let bigint = parseInt(hex.slice(1), 16);
    let r = (bigint >> 16) & 255;
    let g = (bigint >> 8) & 255;
    let b = bigint & 255;
    return [r, g, b];
  };

  // Convert RGB color to hex
  const rgbToHex = (rgb) => {
    return `#${((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2])
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  };

  // Get the RGB values of the two colors
  let [r1, g1, b1] = hexToRgb(color1);
  let [r2, g2, b2] = hexToRgb(color2);

  // Calculate the RGB values for the gradient color
  let r = Math.round(r1 + (r2 - r1) * percentage);
  let g = Math.round(g1 + (g2 - g1) * percentage);
  let b = Math.round(b1 + (b2 - b1) * percentage);

  return rgbToHex([r, g, b]);
};

export const contains = (target, str) => {
  return (target || "").includes(str);
};

export const getPlanShortName = (name) => name?.replace("Surmount ", "") || "";

export const isLocaleEn = (locale) => {
  return contains(locale, FALLBACK_LANG);
};

export const getBrokerIcon = (url, theme) => {
  let _url = url;

  if (theme === "light" && contains(_url, "surmount_paper")) {
    _url = _url.replace("surmount_paper", "surmount_paper_light");
  }
  return _url;
};

export const roundOff = (value, decimals = 2) => {
  if (value && typeof value == "number") {
    return value.toFixed(decimals);
  }
  return 0;
};

export const roundNumber = (value) => {
  if (value && typeof value == "number") {
    return Math.round(value);
  }
  return 0;
};

export const isLoggedIn = () => {
  const token = getItemFromStorage(AUTH_TOKEN_KEY);
  if (token) {
    return true;
  }
  return false;
};

export const getStatusValues = (deploy_status) => {
  return DEPLOY_STATUS[deploy_status];
};

export const transformString = (str) => {
  if (!str?.length) {
    return "";
  }

  let convertedStr = str.charAt(0).toUpperCase() + str.substring(1);
  convertedStr = convertedStr.replace("c", "C");

  if (contains(str, "_")) {
    return convertedStr.split("_").join(" ");
  } else {
    return convertedStr.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
};

export const openNewTab = (url, target = "_blank") => {
  window.open(url, target);
};

export const reloadTab = () => {
  window.location.reload();
};

// DRF fields are usually strings but can arrive as a list or a field->errors
// dict. Anything non-string reaches React as a child and throws, so pull the
// first string out instead.
const firstMessage = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return firstMessage(value[0]);
  if (value && typeof value === "object") {
    return firstMessage(Object.values(value)[0]);
  }
  return undefined;
};

export const parseErrorMessage = (error) => {
  if (error === null) {
    return;
  }

  let msg = error.message;
  const data = error?.response?.data;
  const err = error?.response?.error;

  // DRF puts step-up / permission errors under `detail`; message (when present)
  // is more specific, so it still wins below.
  if (data?.detail) {
    msg = firstMessage(data.detail) ?? msg;
  }
  if (data?.message) {
    msg = firstMessage(data.message) ?? msg;
  }
  if (err?.message) {
    msg = firstMessage(err.message) ?? msg;
  }
  if (data?.email) {
    const emailErrors = data.email || [];
    msg = firstMessage(emailErrors) || "Error Occurred";
  }

  return msg;
};

export const getStatusColor = (status, theme) => {
  if (status === "Active") {
    return COLOR_GREEN;
  } else if (status === "Not connected") {
    return theme === "light" ? "#048af5" : "#0A7EFF";
  }
  return theme === "light" ? "#202C40" : "#ffffff";
};

export const getRandomColor = () => {
  return rcolor();
};

export const isEmptyObj = (obj) => {
  if (!obj) {
    return false;
  }
  return Object.keys(obj).length === 0;
};

export const sleep = ({ timeInms = 1000 }) => {
  return new Promise(function (resolve) {
    setTimeout(function () {
      resolve();
    }, timeInms);
  });
};

export const makeQueryParams = (obj) => {
  return new URLSearchParams(obj).toString();
};

export const getFileExtension = (filename) => {
  if (!filename) {
    return;
  }
  return filename.split(".").pop();
};

export const getCleanLabel = (str) => {
  if (!str) {
    return "";
  }
  return str.split("_").join(" ");
};

export const numberSeparator = (value, decimal = 2) => {
  if (!value) {
    return 0;
  }
  if (typeof value === "number") {
    return value.toLocaleString("en-US", {
      maximumFractionDigits: decimal,
      minimumFractionDigits: decimal,
    });
  }
  return value;
};

export const formatCurrency = (value, decimal = 2) => {
  if (!value) {
    return "$0.00";
  }
  if (typeof value === "number") {
    return `$${value.toLocaleString("en-US", {
      maximumFractionDigits: decimal,
      minimumFractionDigits: decimal,
    })}`;
  }
  return `$${value}`;
};

// minimum_investment comes back as a bare float from some endpoints (e.g.
// trading-connection/create/, community strategies) and as { raw_number,
// formatted } from others (e.g. strategy details, marketplace). Both are
// real, current API shapes — not a hypothetical — so both must be handled.
export const resolveMinimumInvestment = (minimumInvestment) => {
  if (typeof minimumInvestment === "number") {
    return {
      raw: minimumInvestment,
      formatted: formatCurrency(minimumInvestment),
    };
  }
  return {
    raw: minimumInvestment?.raw_number ?? 100,
    formatted: minimumInvestment?.formatted ?? "$100.00",
  };
};

export const hasObjectEmptyValue = (obj) => {
  if (obj === null) {
    return false;
  }
  return !Object.values(obj).every((val) => {
    if (typeof val === "object") {
      return !hasObjectEmptyValue(val);
    }
    return val !== "";
  });
};

export const debounce = (fn, delay) => {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(context, args);
    }, delay);
  };
};

export const isJsonObject = (obj) => {
  if (!obj) {
    return false;
  }
  return obj.constructor == Object;
};

export const scrollToDiv = (id) => {
  document
    .getElementById(id)
    .scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
};

export const getRiskPotentialLabel = (level) => {
  if (level <= 5 / 3) {
    return "Low";
  } else if (level <= 10 / 3) {
    return "Moderate";
  } else if (level <= 5) {
    return "High";
  }
};

export const calcPercentage = (value, total) => {
  return (value / total) * 100;
};

export const trimTooltipString = (string) => {
  if (!string) return "";
  const length = 600;
  return string.length > length
    ? string.substring(0, length - 3) + "..."
    : string.substring(0, length);
};

export const isChildRoute = (pathname) => {
  const count = (pathname?.match(/\//g) || []).length;

  if (count > 1) {
    return true;
  }

  return false;
};

export const getPercentage = (percent, total) => {
  return (percent / 100) * total;
};

export const getUnique = (data, key) => {
  return [...new Set(data.map((item) => item[key]))];
};

export const toTitleCase = (str) => {
  if (!str) {
    return "";
  }
  let splitStr = str.toLowerCase().split(" ");
  for (var i = 0; i < splitStr.length; i++) {
    splitStr[i] =
      splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  return splitStr.join(" ");
};

export const checkAndUpdateArray = (arr, str) => {
  const index = arr.indexOf(str);

  if (index !== -1) {
    // String exists in the array, remove it
    arr.splice(index, 1);
  } else {
    // String doesn't exist in the array, add it
    arr.push(str);
  }

  return arr;
};

export const getStockCompanyIcon = (symbol) => {
  // return `https://g.foolcdn.com/art/companylogos/mark/${symbol}.png`;
  return `https://financialmodelingprep.com/image-stock/${symbol}.png`;
};

export const getChartColor = (usedColors) => {
  // Filter out used colors from the set of available colors
  const availableColors = CHART_COLORS.filter(
    (color) => !contains(usedColors, color)
  );

  if (availableColors.length > 0) {
    // If there are available colors, pick a random one
    const randomIndex = Math.floor(Math.random() * availableColors.length);
    return availableColors[randomIndex];
  } else {
    // If all colors have been used, generate a gradient color from the palette
    const color1 =
      CHART_COLORS[Math.floor(Math.random() * CHART_COLORS.length)];
    const color2 =
      CHART_COLORS[Math.floor(Math.random() * CHART_COLORS.length)];
    const percentage = Math.random(); // Random percentage for the gradient
    return getGradientColor(color1, color2, percentage);
  }
};

export const hasPlan = (plans = []) => {
  const isMonetizationInActive = getItemFromStorage(MONETIZATION_KEY);
  // dummy_premium = true
  if (isMonetizationInActive) {
    return true;
  }

  const planId = getItemFromStorage(USER_PLAN_KEY);
  const planIdParam = new URLSearchParams(location.search).get("plan_id");
  const currentPlan = (planIdParam || planId || "").toLowerCase();
  return plans.some((plan) => contains(currentPlan, plan));
};

export const hasPremium = () => hasPlan(["premium"]);
export const hasFreeOrCore = () => {
  const userType = getItemFromStorage(USER_TYPE_KEY);
  if (userType === "PARTNERSHIP" || userType === "GRANDFATHERED") return false;
  return hasPlan(["free", "core"]);
};

export const isPremiumUser = () => {
  const userType = getItemFromStorage(USER_TYPE_KEY);

  return (
    hasPremium() || userType === "GRANDFATHERED" || userType === "PARTNERSHIP"
  );
};

export const isFreeUser = () => {
  const userType = getItemFromStorage(USER_TYPE_KEY);
  if (userType === "PARTNERSHIP" || userType === "GRANDFATHERED") return false;
  return userType === "FREE";
};

// Unlike isPremiumUser(), admin state is not mirrored into storage, so the
// user object from `state.layout.user` has to be passed in.
export const isAdmin = (user, permission) => {
  if (!user?.is_admin) {
    return false;
  }
  if (!permission) {
    return true;
  }
  return (user.admin_permissions || []).includes(permission);
};

export const isIBKR = (broker) => {
  if (broker.name === "Interactive Brokers") {
    return true;
  }
};

export const calculateStrategyTotals = (strategies) => {
  const totals = {
    trending: 0,
    popular: 0,
    featured: 0,
  };

  strategies.forEach((strategy) => {
    if (strategy.strategy_label) {
      totals[strategy.strategy_label] =
        (totals[strategy.strategy_label] || 0) + 1;
    }
  });

  return totals;
};

export const isMobileDevice = () => {
  return window.innerWidth < 768;
};

export const trimmedName = (name) => {
  if (name?.length > 0) {
    name = name.charAt(0);
  }
  return name;
};

export const handleDownloadLink = (url) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.download = url.split("/").pop();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const capitalizeWords = (text) => {
  if (!text) return "";
  return text
    .split(", ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(", ");
};

export const matchDynamicRoute = (pathname, routePattern) => {
  if (!routePattern.includes(":id")) {
    return pathname === routePattern;
  }

  const regexPattern = routePattern.replace(":id", "[^/]+");
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(pathname);
};

// Re-export date utility functions for convenience
export { parseDateISO, formatDateSuffix } from "./date";

export const formatCompactNumber = (number) => {
  const absValue = Math.abs(number);
  if (absValue === 0) return "0";
  if (absValue >= 1.0e9) return `$${(absValue / 1.0e9).toFixed(1)}B`;
  if (absValue >= 1.0e6) return `$${(absValue / 1.0e6).toFixed(1)}M`;
  if (absValue >= 1.0e3) return `$${(absValue / 1.0e3).toFixed(0)}K`;
  return `$${absValue.toFixed(0)}`;
};
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + " " + sizes[i];
};

/**
 * Returns a new array with duplicate items removed, using the value returned
 * by `keyFn` as the uniqueness key. First occurrence wins.
 */
export const deduplicateBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter((item) => {
    const key = keyFn(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
